import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const SESSION_KEY = "2am:intro-played";

/**
 * Black curtain that rises on first load, then disappears.
 * - Plays once per browser session (sessionStorage flag).
 * - Fully skipped under prefers-reduced-motion.
 */
export function IntroOverlay() {
  const reduced = useReducedMotion();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (reduced) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      // storage may be unavailable; fall back to showing once per mount
    }
    setShow(true);
    // Lock scroll while the curtain is up
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = window.setTimeout(() => {
      document.body.style.overflow = prev;
      setShow(false);
    }, 1100);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(t);
    };
  }, [reduced]);

  if (reduced) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="intro-curtain"
          aria-hidden
          className="fixed inset-0 z-[100] flex items-end justify-start bg-foreground text-background"
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1], delay: 0.35 }}
          style={{ willChange: "transform" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="container-page pb-10 md:pb-14"
          >
            <div className="flex items-baseline gap-3 font-mono text-xs uppercase tracking-[0.18em] text-background/70">
              <span className="tick-dot" style={{ background: "var(--blaze)" }} />
              <span>2AM Hospitality · Loading</span>
            </div>
            <div className="font-display mt-4 text-5xl font-bold leading-[0.95] tracking-[-0.04em] md:text-7xl">
              When the lobby
              <br />
              goes <span className="accent-blaze">quiet.</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}